import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { BonusCampaign } from '../../../api/fetchers/bonusPromotions/types/bonus';
import { BonusStatus } from '../../../api/fetchers/bonusPromotions/types/constants';
import {
  fetchBonusCampaignsAction,
  fetchBonusHistoryAction,
  postAckBonusAction,
  postClaimBonusAction,
} from '../../../store/apiActions/fetchBonusPromotionAction';

import type { BonusHydrateState, BonusPromoState } from './types';
import { updateBonusCampaignState } from './utils';

export const bonusPromotionsSlice = createSlice({
  name: 'bonusPromotions',
  initialState: {
    activeBonusCampaign: null,
    eligibleBonusCampaign: null,
    cancelledBonusCampaigns: [],
    wageredBonusCampaigns: [],
    expiredBonusCampaigns: [],
    isBonusPromotionsFetching: false,
    isAcknowledged: false,
    isBonusHistoryLoading: false,
    isBonusPromotionsInitialised: false,
    silentAckBonusIds: [],
    forceBonusRefresh: false,
    refetchCampaign: false,
  } as BonusPromoState,
  reducers: {
    hydrateSlice: (state, action: PayloadAction<BonusHydrateState>) => {
      state.envBonusPromo = action.payload.envBonusPromo;
      state.forceBonusRefresh = !!action.payload.refreshBonus;
    },
    setIsBonusPromotionsFetching: (state, action: PayloadAction<boolean>) => {
      state.isBonusPromotionsFetching = action.payload;
    },
    setIsAcknowledged: (state, action: PayloadAction<boolean>) => {
      state.isAcknowledged = action.payload;
    },
    setIsBonusHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.isBonusHistoryLoading = action.payload;
    },
    setSilentAckBonusIds: (state, action: PayloadAction<number[]>) => {
      state.silentAckBonusIds = action.payload;
    },
    setForceRefreshBonus: (state, action: PayloadAction<boolean>) => {
      state.forceBonusRefresh = action.payload;
    },
    updateChipBalance: (state, action: PayloadAction<number>) => {
      if (state.eligibleBonusCampaign) {
        state.eligibleBonusCampaign.bonus.noOfchipsRemaining = action.payload;
        state.eligibleBonusCampaign.bonus.noOfchipsUsed =
          state.eligibleBonusCampaign.bonus.noOfChips - state.eligibleBonusCampaign.bonus.noOfchipsRemaining; // it will not go negative
      }
    },
    updateMyWins: (state, action: PayloadAction<number>) => {
      if (state.eligibleBonusCampaign) {
        state.eligibleBonusCampaign.bonus.myWins = action.payload;
      }
    },
    updateNotifications: (state, action: PayloadAction<BonusStatus>) => {
      switch (action.payload) {
        case BonusStatus.Wagered:
          state.wageredBonusCampaigns.shift();
          break;
        case BonusStatus.Cancelled:
          state.cancelledBonusCampaigns.shift();
          break;
        case BonusStatus.Expired:
          state.expiredBonusCampaigns.shift();
          state.eligibleBonusCampaign = null; // remove active one to disappear in ear.
          break;
        default:
          break;
      }
    },
    updateExpiredNotifications: (state, action: PayloadAction<BonusCampaign>) => {
      state.expiredBonusCampaigns = [
        {
          ...action.payload,
          bonus: { ...action.payload.bonus, status: BonusStatus.Expired },
        },
      ];
    },
    setRefetchCampaign: (state, action: PayloadAction<boolean>) => {
      state.refetchCampaign = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBonusCampaignsAction.fulfilled, (state, action) => {
        const hideCancelNotification = !state.isBonusPromotionsInitialised && !!action.payload.activeBonusCampaign;
        state.isBonusPromotionsFetching = false;
        if (
          state.eligibleBonusCampaign &&
          state.eligibleBonusCampaign.bonus.id !== action.payload.activeBonusCampaign?.bonus.id
        ) {
          state.isAcknowledged = false;
        }
        const transactions = state.eligibleBonusCampaign?.bonus.transactions;
        state.eligibleBonusCampaign = action.payload.activeBonusCampaign;
        if (transactions && state.eligibleBonusCampaign) {
          state.eligibleBonusCampaign.bonus.transactions = transactions;
        }
        state.wageredBonusCampaigns = action.payload.wageredCampaigns;
        state.cancelledBonusCampaigns = action.payload.cancelledCampaigns;
        if (action.payload.activeBonusCampaign?.bonus.status === BonusStatus.Active) {
          state.activeBonusCampaign = action.payload.activeBonusCampaign;
        } else {
          state.activeBonusCampaign = null;
        }
        if (hideCancelNotification) {
          state.cancelledBonusCampaigns = action.payload.cancelledCampaigns.filter(
            (campaign) => campaign.bonus.claimedOn
          );
          state.silentAckBonusIds = action.payload.cancelledCampaigns
            .filter((campaign) => !campaign.bonus.claimedOn)
            .map((value) => value.bonus.id);
        }
        state.isBonusPromotionsInitialised = true;
        state.refetchCampaign = false;
      })
      .addCase(fetchBonusCampaignsAction.rejected, (state) => {
        state.isBonusPromotionsInitialised = true;
      })
      .addCase(postClaimBonusAction.fulfilled, (state, action) => {
        updateBonusCampaignState(state, action.payload);
      })
      .addCase(postAckBonusAction.fulfilled, (state, action) => {
        updateBonusCampaignState(state, action.payload);
      })
      .addCase(fetchBonusHistoryAction.fulfilled, (state, action) => {
        if (state.eligibleBonusCampaign) {
          if (action.payload.bonusHistory.page.number === 0) {
            state.isBonusHistoryLoading = false;
            state.eligibleBonusCampaign.bonus.transactions.data = [];
          }
          state.eligibleBonusCampaign.bonus.transactions.data.push(...action.payload.bonusHistory.content);
          state.eligibleBonusCampaign.bonus.transactions.pageNumber = action.payload.bonusHistory.page.number;
          state.eligibleBonusCampaign.bonus.transactions.totalPages = action.payload.bonusHistory.page.totalPages;
          state.eligibleBonusCampaign.bonus.myWins = action.payload.accumulatedWins;
        }
      });
  },
});

export const bonusPromotionsActions = bonusPromotionsSlice.actions;
